<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMediaQuery } from '@vueuse/core'
import { ChevronDown, ChevronUp, Filter } from 'lucide-vue-next'
import Checkbox from 'primevue/checkbox'
import BBtn from '@lib/components/base/BBtn.vue'
import BSelect from '@lib/components/base/BSelect.vue'
import BDateTimeInput from '@lib/components/base/BDateTimeInput.vue'
import { useEventOptions } from '@/composables/useLookups'
import { useDeviceOptions } from '@/composables/useDeviceOptions'
import type { StatsFilters, Interval } from './statistics-filters'

const props = defineProps<{
    modelValue: StatsFilters
    loading?: boolean
}>()
const emit = defineEmits<{
    (e: 'update:modelValue', v: StatsFilters): void
    (e: 'apply'): void
}>()

const { t } = useI18n()
const isDesktop = useMediaQuery('(min-width: 768px)')
const open = ref(isDesktop.value)

const eventOptions = useEventOptions()
const deviceOptions = useDeviceOptions()

const intervalOptions = computed(() => [
    { value: '', label: t('statistics.filter.intervalOptions.auto') },
    { value: 'day', label: t('statistics.filter.intervalOptions.day') },
    { value: 'hour', label: t('statistics.filter.intervalOptions.hour') },
    { value: 'minute', label: t('statistics.filter.intervalOptions.minute') }
])

function update<K extends keyof StatsFilters>(key: K, value: StatsFilters[K]) {
    emit('update:modelValue', { ...props.modelValue, [key]: value })
}

function onIntervalChange(v: string) {
    update('interval', (v || null) as Interval | null)
}

function onSubmit() {
    emit('apply')
}
</script>

<template>
    <div class="bg-white border border-gray-200 rounded-12 mb-4 overflow-hidden">
        <button
            type="button"
            class="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors duration-fast"
            @click="open = !open"
        >
            <Filter class="w-4 h-4 text-gray-500" />
            <span class="font-medium text-gray-700">{{ t('statistics.filter.toggle') }}</span>
            <component :is="open ? ChevronUp : ChevronDown" class="ml-auto w-4 h-4 text-gray-400" />
        </button>
        <div v-if="open" class="px-4 pb-4 border-t border-gray-200">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                <label class="block">
                    <span class="text-xs text-gray-600 mb-1 block">{{ t('statistics.filter.events') }}</span>
                    <BSelect
                        :model-value="modelValue.eventIds"
                        :options="eventOptions"
                        option-label="name"
                        option-value="id"
                        :placeholder="t('statistics.filter.eventsPlaceholder')"
                        :filter="true"
                        :multiple="true"
                        :show-clear="true"
                        @update:model-value="v => update('eventIds', (v ?? []) as number[])"
                    />
                </label>
                <label class="block">
                    <span class="text-xs text-gray-600 mb-1 block">{{ t('statistics.filter.devices') }}</span>
                    <BSelect
                        :model-value="modelValue.devices"
                        :options="deviceOptions"
                        option-label="name"
                        option-value="id"
                        :placeholder="t('statistics.filter.devicesPlaceholder')"
                        :filter="true"
                        :multiple="true"
                        :show-clear="true"
                        @update:model-value="v => update('devices', (v ?? []) as string[])"
                    />
                </label>
                <label class="block">
                    <span class="text-xs text-gray-600 mb-1 block">{{ t('statistics.filter.interval') }}</span>
                    <BSelect
                        :model-value="modelValue.interval ?? ''"
                        :options="intervalOptions"
                        option-label="label"
                        option-value="value"
                        :show-clear="false"
                        @update:model-value="v => onIntervalChange((v as string) ?? '')"
                    />
                </label>
                <label class="block">
                    <span class="text-xs text-gray-600 mb-1 block">{{ t('statistics.filter.dateFrom') }}</span>
                    <BDateTimeInput
                        :model-value="modelValue.dateFrom"
                        @update:model-value="v => update('dateFrom', v)"
                    />
                </label>
                <label class="block">
                    <span class="text-xs text-gray-600 mb-1 block">{{ t('statistics.filter.dateTo') }}</span>
                    <BDateTimeInput
                        :model-value="modelValue.dateTo"
                        @update:model-value="v => update('dateTo', v)"
                    />
                </label>
                <label class="flex items-center gap-2 cursor-pointer pt-5">
                    <Checkbox
                        :binary="true"
                        :model-value="modelValue.showEmptyTicketsets"
                        @update:model-value="(v: boolean) => update('showEmptyTicketsets', v)"
                    />
                    <span class="text-xs text-gray-700">{{ t('statistics.filter.showEmpty') }}</span>
                </label>
            </div>
            <div class="flex justify-end mt-3">
                <BBtn size="small" :disabled="loading" :label="t('statistics.filter.submit')" @click="onSubmit" />
            </div>
        </div>
    </div>
</template>
